import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useGeoBasedLocale, useUserPickedCurrency } from '@shared/hooks/useGeoLocale';
import { useEventInfo } from '@shared/utils/eventInfo';
import { withWindow } from '@shared/utils/withWindow';
import { DEFAULT_CURRENCY } from './constants';
import { isCurrencyCodeType } from './currency.constants';
import type { CurrencyCodeType } from './currency.types';
import { useStationeryCurrencies } from './useStationeryCurrencies';

/** `en-PL` → `PL`; `undefined` for a bare language like `pl`. */
const regionOf = (locale: string | undefined | null): string | undefined => locale?.split('-')[1]?.toUpperCase();

/**
 * The region a stationery buyer is in, for choosing a currency and for the shipping warning: the event
 * locale's region, else the browser's. `undefined` when neither carries one (a bare `pl`).
 */
export const useStationeryBuyerRegion = (): string | undefined => {
  const { eventInfo } = useEventInfo();
  return regionOf(eventInfo?.locale) ?? regionOf(withWindow(global => global.navigator.language, undefined));
};

/**
 * The currency stationery and Joy Feature checkouts price in, first match wins:
 *
 * 1. The user's own pick (`?currency=` or stored selection), if card_service sells it.
 * 2. The card_service currency whose `countryCodes` include the user's region — the event locale's region,
 *    else the browser's. This is what lets a market added in card_service default to its own currency
 *    without a client change; `useGeoBasedLocale` only knows the regions in its fixed locale table.
 * 3. The geo-locale currency if card_service sells it, otherwise USD.
 *
 * Derived state over `joyLocale`, Joy's neutral locale store. Stores nothing of its own, so a currency
 * changed elsewhere on Joy (registry, travel) propagates here. A pick card_service doesn't sell is skipped
 * rather than stored over. To change the currency, call `updateLocaleCurrency`.
 *
 * While card_service's list loads, the geo currency is returned as-is so prices don't flash through USD;
 * `useCurrencyConversionRate` waits for the list before asking for a rate.
 */
export const useStationeryCurrency = (): CurrencyCodeType => {
  const { currency } = useGeoBasedLocale();
  const pickedCurrency = useUserPickedCurrency();
  const region = useStationeryBuyerRegion();
  const currencies = useStationeryCurrencies();

  if (!currencies) return currency;

  const isSold = (code: string | undefined): code is CurrencyCodeType => isCurrencyCodeType(code) && currencies.some(({ currencyCode }) => currencyCode === code);

  if (isSold(pickedCurrency)) return pickedCurrency;

  const regionalCurrency = region ? currencies.find(({ countryCodes }) => countryCodes.includes(region))?.currencyCode : undefined;
  if (isSold(regionalCurrency)) return regionalCurrency;

  return isSold(currency) ? currency : DEFAULT_CURRENCY;
};

/**
 * True while local-currency pricing is on and card_service's list hasn't loaded, i.e. while
 * {@link useStationeryCurrency} may still change. Checkout CTAs wait on it so an order can't be created in
 * the pre-load currency (e.g. USD for a `pl-PL` visitor about to resolve to PLN).
 */
export const useIsStationeryCurrencyPending = (): boolean => {
  const [isLocalCurrencyEnabled] = useIsFeatureEnabled('printLocalCurrency');
  const currencies = useStationeryCurrencies();
  return Boolean(isLocalCurrencyEnabled) && !currencies;
};
